import {
    Controls,
    ControlsItem,
    Divider,
    BookmarkBorderedIcon,
    ExpandVerticalIcon,
    CollapseVerticalIcon,
    Button
} from '@genestack/ui';
import React from 'react';

import {DocumentTypeLabel} from '../../../../../components/document-type-label';
import {useDocRevisionsContext} from '../../../../../providers/version';
import {LastActionCaption} from '../../../common/last-action-caption';

import {DocumentMenu} from './document-menu';
import {useEditorModel} from '../model';
import {VersionLabel} from '../../../common/version-history/version-label';
import {EditorHeaderSkeleton} from '../../../../../components/editor/editor-header/editor-header-skeleton';
import {EditorHeader} from '../../../../../components/editor/editor-header/editor-header';
import {OntologyElement} from '../../../common/ontology-view/requests/use-ontology-element-query';

interface Props {
    revisionId?: string;
    editorState: ReturnType<typeof useEditorModel>;
    isBodyCollapsed: boolean;
    toggleIsBodyCollapsed: () => void;
    docInfo?: ViewModeDoc;
    isGraphPanelOpen: boolean;
    ontologyElementInfo?: OntologyElement;
}

export const DocumentEditorHeader = ({
    revisionId,
    editorState,
    isBodyCollapsed,
    toggleIsBodyCollapsed,
    isGraphPanelOpen,
    docInfo,
    ontologyElementInfo
}: Props) => {
    const {selectedRevision} = useDocRevisionsContext();

    const docMenuCaption = React.useMemo(
        () =>
            !!editorState.revisionInfo && (
                <>
                    <Divider variant="transparent" />
                    <LastActionCaption lastAction={editorState.revisionInfo} />
                </>
            ),
        [editorState.revisionInfo]
    );

    if (!docInfo) {
        return <EditorHeaderSkeleton />;
    }

    return (
        <EditorHeader
            disableEditingTitle
            data-qa="doc-header"
            titleProps={{
                'data-qa': 'doc-title'
            }}
            menu={
                <DocumentMenu
                    docInfo={docInfo}
                    menuCaption={docMenuCaption}
                    revisionInfo={editorState.revisionInfo}
                />
            }
            name={docInfo.name}
            prepend={
                <Controls gap={1}>
                    {docInfo.userFavorite && (
                        <ControlsItem>
                            <BookmarkBorderedIcon />
                        </ControlsItem>
                    )}
                    <ControlsItem>
                        <DocumentTypeLabel type={docInfo.type} readOnly={docInfo.readOnly} />
                    </ControlsItem>
                </Controls>
            }
            append={
                <Controls gap={1}>
                    {revisionId && selectedRevision ? (
                        <ControlsItem>
                            <VersionLabel selectedVersion={selectedRevision} />
                        </ControlsItem>
                    ) : null}

                    <ControlsItem>
                        <em>{ontologyElementInfo?.name}</em>
                    </ControlsItem>
                </Controls>
            }
        >
            {isGraphPanelOpen && (
                <Controls justify="end">
                    <ControlsItem>
                        <Button
                            size="small"
                            ghost
                            iconEnd={
                                isBodyCollapsed ? <ExpandVerticalIcon /> : <CollapseVerticalIcon />
                            }
                            onClick={toggleIsBodyCollapsed}
                        />
                    </ControlsItem>
                </Controls>
            )}
        </EditorHeader>
    );
};
